import { useState } from 'react';
import { Pencil } from 'lucide-react';

/**
 * What this vendor costs, in the planner's own words.
 *
 * It used to be a resolved value: newest structured extraction, else newest
 * bare reading, else the planner's typed figure — with a rejection cascade for
 * vetoing a reading, a revision rule behind "was $1,000", an `≈` marker for a
 * low-confidence total, a provenance disclosure naming the reply it came from,
 * and a two-step clear where the first press dropped an override and the
 * second struck out the vendor's number. Each rule answered a real question.
 * Together they made a field that changed by itself for reasons its reader
 * could not reconstruct, and the arrival of package trees added one more
 * unanswerable question to every change: *which* of three tiers is this?
 *
 * Now it is a text field. It holds what the planner typed and nothing writes
 * it but them. Everything the extractor reads still exists and is richer than
 * this figure ever was — every quote, every package, every line item, the
 * document it came from — one tab away, where a machine's reading can be
 * looked at rather than silently installed as an answer.
 *
 * It is still drawn as a value in the page rather than as a form control — a
 * box at `text-2xl` competes with the vendor's name for the largest object on
 * the header, and the quote is a fact about this inquiry, not a question being
 * asked. What the box was for is kept: a dotted rule under the digits and a
 * pencil beside them say it can be typed in, which a bare number no longer
 * says now that nothing else ever fills it.
 */

export interface ResolvedQuote {
  amount: number;
  currency: string;
}

/** The symbol, for the currencies this product actually sees. Falls back to
 *  the code, which is wrong for nobody and ugly for few. */
function currencyPrefix(currency: string): string {
  const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$' };
  return symbols[currency] ?? `${currency} `;
}

const format = (amount: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(amount);

export default function QuoteField({
  quote,
  saving,
  onChange,
}: {
  quote: ResolvedQuote | null;
  saving: boolean;
  /** Null clears the field. There is nothing else for it to mean now. */
  onChange: (amount: number | null) => void;
}) {
  /**
   * The keystrokes, while there are any. Null means "show the saved figure",
   * which is what makes an external change — another tab, a failed save
   * rolling back — visible without fighting the planner mid-edit.
   */
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? (quote ? format(quote.amount) : '');

  function commit() {
    if (draft === null) return;
    const typed = draft.trim();
    setDraft(null);

    if (typed === '') {
      if (quote) onChange(null);
      return;
    }

    // Commas and a currency symbol are what a planner pastes out of an email,
    // and refusing them would be refusing the most ordinary way this field
    // gets filled.
    const parsed = Number(typed.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(parsed) || parsed < 0) return;
    if (quote && parsed === quote.amount) return;
    onChange(parsed);
  }

  return (
    <div className="space-y-1">
      <label
        htmlFor="quote-amount"
        className="label-caps block text-[0.6875rem] text-muted-foreground"
      >
        Quote
      </label>

      {/* The figure sits in the page rather than in a box.

          A bordered input made the quote look like a form to fill in, when it
          is a fact about this vendor that happens to be editable — and at
          `text-2xl` the box was the largest object on the header, competing
          with the vendor's own name.

          What the box was buying was the signal that this can be typed in, so
          that is kept and the rest is dropped: a dotted rule under the digits,
          which is the convention for an editable value everywhere else it
          appears, and a pencil beside them. Both firm up on focus, so the
          field confirms it took the caret. */}
      <div className="group inline-flex items-baseline gap-1">
        <span
          aria-hidden
          className={`display-tight text-2xl ${quote ? 'text-foreground' : 'text-muted-foreground/60'}`}
        >
          {currencyPrefix(quote?.currency ?? 'USD')}
        </span>
        <input
          id="quote-amount"
          inputMode="decimal"
          autoComplete="off"
          disabled={saving}
          value={value}
          placeholder="Add"
          onFocus={() => setDraft(quote ? String(quote.amount) : '')}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(null);
              e.currentTarget.blur();
            }
          }}
          /* Tabular figures so a corrected amount does not shuffle the digits
             sideways as it changes. */
          className="display-tight w-[6ch] border-b-2 border-dotted border-muted-foreground/40
            bg-transparent pb-0.5 text-2xl tabular-nums text-foreground outline-none
            transition-colors placeholder:text-[1.125rem] placeholder:text-muted-foreground/60
            group-hover:border-muted-foreground/70
            focus:border-solid focus:border-primary
            disabled:cursor-not-allowed disabled:opacity-60"
        />
        <Pencil
          aria-hidden
          className="size-3 self-center text-muted-foreground/50 transition-colors
            group-hover:text-muted-foreground group-focus-within:text-primary"
        />
      </div>
    </div>
  );
}
